// ============================================================
// AppShell.jsx — Full post-login app
// AI chatbot-style analyze center + sidebar + right panel
// Includes: test plans, script downloads, screenshots in reports,
//           OAuth for GitHub/GitLab/Azure DevOps
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import {
  Sparkles, Play, Download, FileText, FileSpreadsheet,
  FileCode, Upload, Plus, Trash2, CheckCircle, XCircle,
  Bug, RotateCcw, ChevronDown, ChevronUp, ChevronRight, Search,
  BarChart2, LogOut, Crown, Globe, Github, GitBranch,
  Database, Lock, Monitor, Code, TrendingUp, X,
  Layers, FileUp, Cloud, Terminal, Image, Package,
  RefreshCw, Clock, Copy, ExternalLink, AlertTriangle,
  Zap, Settings,
} from 'lucide-react';
import { getTheme, THEMES } from '../lib/theme';
import { getAllTestPlans, generateScript } from '../lib/scriptGenerator';

// ── Design tokens ─────────────────────────────────────────────
// C is updated dynamically by AppShell via updateTheme()
let C = {
  bg:       '#f8fafc',
  bgAlt:    '#ffffff',
  bgCard:   '#ffffff',
  bgHover:  '#f1f5f9',
  sidebar:  '#ffffff',
  panel:    '#f8fafc',
  border:   'rgba(0,0,0,0.08)',
  borderAcc:'rgba(0,179,140,0.3)',
  teal:     '#00b38c',
  tealDim:  'rgba(0,179,140,0.1)',
  blue:     '#2563eb',
  white:    '#0f172a',
  gray:     '#475569',
  grayLight:'#64748b',
};

function updateTheme(T) {
  if (!T) return;
  C.bg       = T.bg;
  C.panel    = T.bgAlt   || T.bg;
  C.card   = T.bgCard  || T.bg;
  C.bg  = T.bgHover || T.bg;
  C.sidebar  = T.sidebar || T.bg;
  C.panel    = T.bgAlt   || T.bg;
  C.border   = T.border;
  C.border= T.borderAcc || T.border;
  C.teal     = T.accent;
  C.tealDim  = T.accentDim;
  C.blue     = T.blue;
  C.white    = T.text;
  C.gray     = T.textSub;
  C.grayLight= T.textSub;
}

const TYPES = ['ui','api','security','performance','database','unit'];
const TYPE_COLORS = { ui:'#3b82f6', api:'#00d4aa', security:'#f59e0b', performance:'#a78bfa', database:'#34d399', unit:'#fb7185' };
const TYPE_ICONS  = { ui:Monitor, api:Globe, security:Lock, performance:TrendingUp, database:Database, unit:Code };

// ── Download helper ───────────────────────────────────────────
// Open screenshot in a new tab using a Blob URL (base64 data URLs are blocked by browsers)
function openScreenshot(dataUrl, title) {
  try {
    // Convert base64 data URL to blob
    const [header, b64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] || 'image/png';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    const blobUrl = URL.createObjectURL(blob);
    // Open in new tab — wrap in an HTML page so it displays nicely
    const html = '<!DOCTYPE html><html><head><title>' + (title||'Screenshot') + '</title>'
      + '<style>*{margin:0;padding:0;background:#0a0f1e}body{display:flex;align-items:center;justify-content:center;min-height:100vh}'
      + 'img{max-width:100%;max-height:100vh;object-fit:contain;border-radius:8px}'
      + 'h3{position:fixed;top:16px;left:50%;transform:translateX(-50%);color:#00d4aa;font-family:sans-serif;font-size:13px;background:rgba(0,0,0,0.7);padding:6px 14px;border-radius:20px}</style></head>'
      + '<body><h3>' + (title||'Screenshot') + '</h3><img src="' + blobUrl + '" onload="URL.revokeObjectURL(this.src)" alt="screenshot"/></body></html>';
    const htmlBlob = new Blob([html], { type:'text/html' });
    const htmlUrl  = URL.createObjectURL(htmlBlob);
    const win = window.open(htmlUrl, '_blank');
    if (win) setTimeout(() => URL.revokeObjectURL(htmlUrl), 3000);
  } catch (e) {
    // Fallback: just try opening directly
    window.open(dataUrl, '_blank');
  }
}


function dl(content, filename, mime = 'text/plain') {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href:url, download:filename, style:'display:none' });
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 300);
}

function dlScreenshot(dataUrl, name) {
  if (!dataUrl) return;
  const a = Object.assign(document.createElement('a'), {
    href: dataUrl, download: `${(name||'screenshot').replace(/[^a-z0-9]/gi,'_')}.png`, style:'display:none',
  });
  document.body.appendChild(a); a.click();
  setTimeout(() => document.body.removeChild(a), 300);
}

// ─────────────────────────────────────────────────────────────
// SPINNER
// ─────────────────────────────────────────────────────────────
function Spinner() {
  const [f, setF] = useState(0);
  const frames = ['⠋','⠙','⠹','⠸','⠼','⠴','⠦','⠧','⠇','⠏'];
  useEffect(() => {
    const t = setInterval(() => setF(n => (n+1)%frames.length), 80);
    return () => clearInterval(t);
  }, []);
  return <span style={{ color:'#00d4aa', fontSize:14, fontFamily:'monospace' }}>{frames[f]}</span>;
}

// ─────────────────────────────────────────────────────────────
// SIDEBAR
// ─────────────────────────────────────────────────────────────
function Sidebar({ active, setActive, userProfile, userPlan, onLogout, onUpgrade, onReports, onApiKeys, totalTests, results, T, theme, setTheme }) {
  const [showThemes, setShowThemes] = useState(false);
  const nav = [
    { id:'analyze',      icon:Sparkles,    label:'Analyze',      badge:null },
    { id:'tests',        icon:Layers,      label:'Test Cases',   badge:totalTests||null },
    { id:'results',      icon:CheckCircle, label:'Results',      badge:results.length||null },
    { id:'plans',        icon:Package,     label:'Test Plans',   badge:null },
    { id:'create',       icon:Plus,        label:'Create Test',  badge:null },
    { id:'template',     icon:FileUp,      label:'Templates',    badge:null },
    { id:'integrations', icon:Cloud,       label:'Integrations', badge:null },
  ];

  return (
    <aside style={{ width:220, flexShrink:0, background:C.sidebar, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', height:'100vh', position:'sticky', top:0, boxShadow:'none' }}>
      {/* Logo */}
      <div style={{ padding:'18px 16px 12px', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:30, height:30, borderRadius:8, background:`linear-gradient(135deg,${C.teal},${C.blue})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>⚡</div>
          <span style={{ color:C.white, fontWeight:800, fontSize:16, letterSpacing:'-0.5px' }}>TestIt<span style={{ color:C.teal }}>Now</span></span>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex:1, padding:'10px 8px', overflowY:'auto' }}>
        <div style={{ color:C.gray, fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', padding:'6px 8px 4px' }}>Workspace</div>
        {nav.map(item => {
          const Icon = item.icon;
          const on   = active === item.id;
          return (
            <button key={item.id} onClick={() => setActive(item.id)} style={{
              width:'100%', display:'flex', alignItems:'center', gap:10,
              padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer',
              background: on ? C.tealDim : 'transparent',
              color: on ? C.teal : C.grayLight,
              fontSize:13, fontWeight: on ? 600 : 400, marginBottom:2,
              borderLeft:`2px solid ${on ? C.teal : 'transparent'}`,
              transition:'all 0.15s',
            }}>
              <Icon size={14}/>
              <span style={{ flex:1, textAlign:'left' }}>{item.label}</span>
              {item.badge ? <span style={{ background: on ? C.teal : C.border, color: on ? '#fff' : C.gray, fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:10 }}>{item.badge}</span> : null}
            </button>
          );
        })}

        <div style={{ marginTop:14 }}>
          <div style={{ color:C.gray, fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', padding:'6px 8px 4px' }}>Reports</div>
          <button onClick={onReports} style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', background:'transparent', color:C.grayLight, fontSize:13 }}>
            <BarChart2 size={14}/> Reports & Export
          </button>
          <button onClick={onApiKeys} style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', background:'transparent', color:C.grayLight, fontSize:13 }}>
            <Settings size={14}/> API Keys
          </button>
        </div>

        {/* Theme picker */}
        <div style={{ marginTop:14 }}>
          <div style={{ color:C.gray, fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', padding:'6px 8px 4px' }}>Appearance</div>
          <button onClick={()=>setShowThemes(p=>!p)} style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', background:'transparent', color:C.grayLight, fontSize:13 }}>
            <span>{THEMES[theme]?.icon}</span>
            <span style={{ flex:1, textAlign:'left' }}>{THEMES[theme]?.name}</span>
            <span style={{ fontSize:10 }}>▾</span>
          </button>
          {showThemes && (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'6px', margin:'4px 0', boxShadow:'none' }}>
              {Object.entries(THEMES).map(([key, th]) => (
                <button key={key} onClick={()=>{ setTheme(key); setShowThemes(false); }} style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:7, border:'none', cursor:'pointer', background: theme===key ? C.tealDim : 'transparent', color: theme===key ? C.tealText : C.grayLight, fontSize:12, fontWeight: theme===key?700:400, textAlign:'left' }}>
                  <span>{th.icon}</span> {th.name} {theme===key&&<span style={{ marginLeft:'auto', fontSize:10 }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* User */}
      <div style={{ borderTop:`1px solid ${C.border}`, padding:'10px 8px' }}>
        {userPlan === 'free' && (
          <button onClick={onUpgrade} style={{ width:'100%', padding:'8px', borderRadius:8, border:'none', background:C.tealDim, color:C.tealText, fontSize:12, fontWeight:700, cursor:'pointer', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
            <Crown size={13}/> Upgrade to Pro
          </button>
        )}
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 8px' }}>
          <div style={{ width:28, height:28, borderRadius:'50%', background:`linear-gradient(135deg,${C.teal},${C.blue})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff', flexShrink:0 }}>
            {(userProfile?.name||'U')[0].toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:C.white, fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{userProfile?.name||'User'}</div>
            <div style={{ color:C.gray, fontSize:10, textTransform:'capitalize' }}>{userPlan} plan</div>
          </div>
          <button onClick={onLogout} style={{ background:'none', border:'none', color:C.gray, cursor:'pointer', padding:4 }} title="Sign out"><LogOut size={13}/></button>
        </div>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────
// ANALYZE HERO — center stage
// ─────────────────────────────────────────────────────────────
function AnalyzeHero({ onAnalyze, isGenerating, progress, progressMsg, totalTests, setActive, activeTab, setActiveTab, tests, results, summary, isRunning, currentTest, onRun, canRun, T }) {
  const [url,         setUrl]         = useState('');
  const [srcType,     setSrcType]     = useState('url');
  const [owner,       setOwner]       = useState('');
  const [repo,        setRepo]        = useState('');
  const [branch,      setBranch]      = useState('main');
  const [token,       setToken]       = useState('');
  const [focused,     setFocused]     = useState(false);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [showAuth,    setShowAuth]    = useState(false);
  const [authUser,    setAuthUser]    = useState('');
  const [authPass,    setAuthPass]    = useState(''); // { provider, user }

  const srcTabs = [
    { id:'url',    icon:'🌐', label:'Website' },
    { id:'github', icon:'🐙', label:'GitHub' },
    { id:'gitlab', icon:'🦊', label:'GitLab' },
    { id:'azure',  icon:'☁️', label:'Azure DevOps' },
    { id:'upload', icon:'📁', label:'Upload' },
  ];

  // ── OAuth sign-in — all providers route through TestItNow's
  // registered OAuth apps on the backend. Users just click
  // "Authorize TestItNow" in the popup — no setup required.
  const handleOAuth = (provider) => {
    const API = process.env.REACT_APP_API_URL || 'https://testitnow-prod.vercel.app';
    const redirectUri = encodeURIComponent(`${window.location.origin}/oauth/callback?provider=${provider}`);

    // Provider-specific authorize URLs — client IDs live on backend only
    const authorizeUrls = {
      github: `https://github.com/login/oauth/authorize?client_id=${process.env.REACT_APP_GITHUB_CLIENT_ID}&scope=repo,read:user&redirect_uri=${redirectUri}&state=${provider}`,
      gitlab: `https://gitlab.com/oauth/authorize?client_id=${process.env.REACT_APP_GITLAB_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code&scope=read_repository+read_user&state=${provider}`,
      azure:  `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize?client_id=${process.env.REACT_APP_AZURE_CLIENT_ID}&response_type=code&redirect_uri=${redirectUri}&scope=vso.work+vso.code&state=${provider}`,
    };

    // If client ID not yet configured, show clear instructions
    const clientIds = {
      github: process.env.REACT_APP_GITHUB_CLIENT_ID,
      gitlab: process.env.REACT_APP_GITLAB_CLIENT_ID,
      azure:  process.env.REACT_APP_AZURE_CLIENT_ID,
    };

    if (!clientIds[provider] || clientIds[provider].startsWith('YOUR_')) {
      alert(
        `To enable ${provider.charAt(0).toUpperCase()+provider.slice(1)} OAuth for all users:\n\n` +
        `1. Go to ${provider === 'github' ? 'github.com → Settings → Developer settings → OAuth Apps' : provider === 'gitlab' ? 'gitlab.com → User Settings → Applications' : 'portal.azure.com → App registrations'}\n` +
        `2. Create an OAuth app named "TestItNow"\n` +
        `3. Set callback URL to: ${window.location.origin}/oauth/callback?provider=${provider}\n` +
        `4. Copy the Client ID → add as REACT_APP_${provider.toUpperCase()}_CLIENT_ID in Vercel frontend env\n` +
        `5. Copy the Client Secret → add as ${provider.toUpperCase()}_CLIENT_SECRET in Vercel backend env\n\n` +
        `For now, paste your Personal Access Token below.`
      );
      return;
    }

    // Open the provider's authorize page in a popup
    const popup = window.open(authorizeUrls[provider], `${provider}_oauth`, 'width=600,height=700,scrollbars=yes');

    // Listen for the token posted back from OAuthCallback.jsx
    const handler = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'oauth_success' && e.data.provider === provider) {
        window.removeEventListener('message', handler);
        if (popup && !popup.closed) popup.close();
        if (e.data.error) {
          alert(`${provider} connection failed: ${e.data.message || e.data.error}`);
          return;
        }
        setToken(e.data.token || '');
        setOauthStatus({ provider, user: e.data.user || '' });
      }
    };
    window.addEventListener('message', handler);

    // Cleanup listener if popup closed manually
    const checkClosed = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handler);
      }
    }, 1000);
  };

  const handleSubmit = () => {
    if (isGenerating) return;
    const authCredentials = (authUser && authPass) ? { username: authUser, password: authPass } : null;
    if (srcType === 'url') {
      if (!url.trim()) return;
      let u = url.trim();
      if (!u.startsWith('http')) u = 'https://' + u;
      onAnalyze({ type:'url', url:u, authCredentials });
    } else if (['github','gitlab','azure'].includes(srcType)) {
      let o = owner, r = repo;
      if (!o && url.includes('/')) {
        const parts = url.replace(/^https?:\/\/[^/]+\//,'').split('/');
        o = parts[0]; r = parts[1];
      }
      if (!o || !r) return alert('Enter owner and repository name');
      onAnalyze({ type:'repo', provider:srcType === 'azure' ? 'azure' : srcType, repoOwner:o, repoName:r, branch, accessToken:token, authCredentials });
    }
  };

  const hasTests = totalTests > 0;

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', background:C.bg, overflowY:'auto' }}>
      {/* Glow */}
      <div style={{ position:'fixed', top:'20%', left:'50%', transform:'translate(-50%,-50%)', width:700, height:300, borderRadius:'50%', background:'radial-gradient(ellipse, rgba(0,212,170,0.04) 0%, transparent 70%)', pointerEvents:'none', zIndex:0 }}/>

      <div style={{ position:'relative', zIndex:1, padding: hasTests ? '24px 32px 0' : '0 32px', flex:1, display:'flex', flexDirection:'column', justifyContent: hasTests ? 'flex-start' : 'center', alignItems:'center', minHeight: hasTests ? 'auto' : '100vh' }}>

        {!hasTests && (
          <>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(0,212,170,0.08)', border:`1px solid ${C.border}`, borderRadius:20, padding:'5px 14px', marginBottom:24 }}>
              <Sparkles size={12} style={{ color:C.teal }}/>
              <span style={{ color:C.teal, fontSize:12, fontWeight:600, letterSpacing:'0.05em' }}>AI-POWERED TEST GENERATION</span>
            </div>
            <h1 style={{ fontSize:'clamp(28px,4vw,52px)', fontWeight:900, color:C.white, letterSpacing:'-2px', marginBottom:12, lineHeight:1.05, textAlign:'center' }}>
              What would you like to test?
            </h1>
            <p style={{ color:C.gray, fontSize:15, marginBottom:36, textAlign:'center', maxWidth:480 }}>
              Website · GitHub · GitLab · Azure DevOps · Source upload<br/>
              Get 1,000+ test cases in under 60 seconds.
            </p>
          </>
        )}

        {/* Source type pills */}
        <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap', justifyContent:'center' }}>
          {srcTabs.map(s => (
            <button key={s.id} onClick={() => setSrcType(s.id)} style={{
              padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer',
              background: srcType===s.id ? C.teal : 'rgba(255,255,255,0.06)',
              color: srcType===s.id ? C.bg : C.grayLight,
              fontSize:12, fontWeight: srcType===s.id ? 700 : 400, transition:'all 0.2s',
            }}>{s.icon} {s.label}</button>
          ))}
        </div>

        {/* OAuth connected badge */}
        {oauthStatus && (
          <div style={{ marginBottom:10, display:'inline-flex', alignItems:'center', gap:8, background:'rgba(0,212,170,0.1)', border:`1px solid rgba(0,212,170,0.3)`, borderRadius:20, padding:'5px 14px' }}>
            <CheckCircle size={13} style={{ color:C.teal }}/>
            <span style={{ color:C.teal, fontSize:12, fontWeight:600 }}>Connected to {oauthStatus.provider} {oauthStatus.user ? `as ${oauthStatus.user}` : ''}</span>
            <button onClick={() => { setOauthStatus(null); setToken(''); }} style={{ background:'none', border:'none', color:C.gray, cursor:'pointer', padding:2 }}><X size={11}/></button>
          </div>
        )}

        {/* Main input box */}
        <div style={{ width:'100%', maxWidth:680, background:C.card, border:`1.5px solid ${focused ? C.teal : C.border}`, borderRadius:16, overflow:'hidden', boxShadow: focused ? `0 0 0 3px rgba(0,212,170,0.1)` : 'none', transition:'all 0.2s' }}>

          {/* URL / top row */}
          {srcType !== 'upload' && (
            <div style={{ display:'flex', alignItems:'center', padding:'14px 18px', gap:12 }}>
              <span style={{ fontSize:18 }}>{srcTabs.find(s=>s.id===srcType)?.icon}</span>
              <input value={url} onChange={e=>setUrl(e.target.value)}
                onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
                onKeyDown={e=>e.key==='Enter'&&handleSubmit()}
                placeholder={{
                  url:'https://yourapp.com',
                  github:'https://github.com/owner/repo or just owner/repo',
                  gitlab:'https://gitlab.com/namespace/project or namespace/project',
                  azure: 'https://dev.azure.com/org/project',
                }[srcType]}
                disabled={isGenerating}
                style={{ flex:1, background:'none', border:'none', outline:'none', color:C.white, fontSize:15, fontFamily:'inherit' }}/>
              <button onClick={handleSubmit} disabled={isGenerating} style={{
                background: isGenerating ? 'rgba(0,212,170,0.25)' : `linear-gradient(135deg,${C.teal},${C.blue})`,
                border:'none', color:C.bg, padding:'9px 18px', borderRadius:9,
                fontSize:13, fontWeight:700, cursor: isGenerating ? 'not-allowed' : 'pointer',
                display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap',
              }}>
                {isGenerating ? <><Spinner/> Analyzing…</> : <><Sparkles size={13}/> Analyze</>}
              </button>
            </div>
          )}

          {/* GitHub/GitLab/Azure extra fields */}
          {['github','gitlab','azure'].includes(srcType) && (
            <div style={{ borderTop:`1px solid ${C.border}`, display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:0 }}>
              {[
                { ph:`${srcType === 'azure' ? 'Organization' : 'Owner'} / namespace`, val:owner, set:setOwner },
                { ph:`${srcType === 'azure' ? 'Project' : 'Repository'} name`,       val:repo,  set:setRepo  },
                { ph:'Branch (default: main)',                                         val:branch, set:setBranch },
              ].map((f,i) => (
                <input key={i} value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.ph}
                  style={{ background:'none', border:'none', borderRight: i<2?`1px solid ${C.border}`:'none', outline:'none', color:C.white, fontSize:12, padding:'10px 14px', fontFamily:'inherit' }}/>
              ))}
            </div>
          )}

          {/* OAuth buttons + PAT */}
          {['github','gitlab','azure'].includes(srcType) && (
            <div style={{ borderTop:`1px solid ${C.border}`, padding:'12px 16px', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <span style={{ color:C.gray, fontSize:12 }}>Sign in:</span>
              <button onClick={() => handleOAuth(srcType)} style={{
                display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8,
                border:`1px solid ${C.border}`, background:C.bg,
                color:C.white, fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.2s',
              }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=C.teal}
              onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                {{github:<Github size={13}/>, gitlab:<GitBranch size={13}/>, azure:<Cloud size={13}/>}[srcType]}
                Connect {srcType === 'github' ? 'GitHub' : srcType === 'gitlab' ? 'GitLab' : 'Azure DevOps'}
              </button>
              <span style={{ color:C.gray, fontSize:11 }}>or paste a Personal Access Token:</span>
              <input value={token} onChange={e=>setToken(e.target.value)} type="password"
                placeholder={`${srcType === 'github' ? 'ghp_' : srcType === 'gitlab' ? 'glpat-' : ''}Personal Access Token`}
                style={{ flex:1, minWidth:160, background:C.bg, border:`1px solid ${C.border}`, borderRadius:7, color:C.white, fontSize:12, padding:'6px 10px', outline:'none', fontFamily:'inherit' }}/>
            </div>
          )}

          {/* Auth credentials — for sites requiring login */}
          {srcType !== 'upload' && (
            <>
              <div
                onClick={() => setShowAuth(v => !v)}
                style={{ borderTop:`1px solid ${C.border}`, padding:'8px 18px', display:'flex', alignItems:'center', gap:8, cursor:'pointer', userSelect:'none' }}>
                <Lock size={12} style={{ color: showAuth ? C.teal : C.gray }}/>
                <span style={{ color: showAuth ? C.teal : C.gray, fontSize:12, fontWeight:600 }}>
                  Authentication {authUser ? `(${authUser})` : '— optional, for protected pages'}
                </span>
                <span style={{ marginLeft:'auto', color:C.gray, fontSize:11 }}>{showAuth ? '▲' : '▼'}</span>
              </div>
              {showAuth && (
                <div style={{ borderTop:`1px solid ${C.border}`, padding:'12px 18px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, background:`rgba(0,212,170,0.03)` }}>
                  <div>
                    <label style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:5 }}>Username / Email</label>
                    <input
                      value={authUser}
                      onChange={e => setAuthUser(e.target.value)}
                      placeholder="admin@yourapp.com"
                      autoComplete="off"
                      style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, color:C.white, fontSize:13, padding:'8px 12px', outline:'none', fontFamily:'inherit' }}/>
                  </div>
                  <div>
                    <label style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:5 }}>Password</label>
                    <input
                      value={authPass}
                      onChange={e => setAuthPass(e.target.value)}
                      type="password"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, color:C.white, fontSize:13, padding:'8px 12px', outline:'none', fontFamily:'inherit' }}/>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <p style={{ color:C.gray, fontSize:11, lineHeight:1.6 }}>
                      <span style={{ color:C.teal }}>🔒</span> Credentials are used only to crawl authenticated pages. They are sent directly to the backend crawler and never stored.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Upload */}
          {srcType === 'upload' && (
            <label style={{ display:'block', padding:'32px', textAlign:'center', cursor:'pointer' }}>
              <FileUp size={28} style={{ color:C.gray, marginBottom:8 }}/>
              <p style={{ color:C.grayLight, fontSize:14, marginBottom:4 }}>Click to upload source files</p>
              <p style={{ color:C.gray, fontSize:12 }}>JS · TS · Python · Go · Java · SQL · YAML</p>
              <input type="file" multiple accept=".js,.ts,.jsx,.tsx,.py,.go,.java,.sql,.yaml,.yml,.md" style={{ display:'none' }}
                onChange={e => {
                  const files = Array.from(e.target.files);
                  Promise.all(files.map(f => new Promise(res => {
                    const r = new FileReader();
                    r.onload = ev => res({ filename:f.name, content:ev.target.result, size:f.size });
                    r.readAsText(f);
                  }))).then(results => onAnalyze({ type:'upload', files:results, label:files[0]?.name }));
                }}/>
            </label>
          )}
        </div>

        {/* Progress */}
        {isGenerating && (
          <div style={{ width:'100%', maxWidth:680, marginTop:16, background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 18px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <Spinner/>
              <span style={{ color:C.grayLight, fontSize:13, flex:1 }}>{progressMsg||'Analyzing…'}</span>
              <span style={{ color:C.teal, fontSize:12, fontFamily:'monospace' }}>{progress}%</span>
            </div>
            <div style={{ background:C.bg, borderRadius:4, height:4 }}>
              <div style={{ height:'100%', borderRadius:4, background:`linear-gradient(90deg,${C.teal},${C.blue})`, width:`${progress}%`, transition:'width 0.5s ease' }}/>
            </div>
          </div>
        )}

        {/* Quick examples */}
        {!hasTests && !isGenerating && srcType === 'url' && (
          <div style={{ marginTop:20, display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
            <span style={{ color:C.gray, fontSize:12 }}>Try:</span>
            {['https://github.com','https://vercel.com','https://stripe.com'].map(ex => (
              <button key={ex} onClick={() => setUrl(ex)} style={{ background:C.bg, border:`1px solid ${C.border}`, color:C.grayLight, padding:'4px 12px', borderRadius:20, fontSize:12, cursor:'pointer' }}>{ex}</button>
            ))}
          </div>
        )}

        {/* Stats grid when tests exist */}
        {hasTests && (
          <div style={{ width:'100%', marginTop:20, paddingBottom:32 }}>
            {/* Result summary bar */}
            {results.length > 0 && (
              <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                {[
                  { label:'Total',   val:summary.total,  color:C.white },
                  { label:'Passed',  val:summary.passed, color:'#4ade80' },
                  { label:'Failed',  val:summary.failed, color:'#f87171' },
                  { label:'Rate',    val:`${summary.total > 0 ? Math.round(summary.passed/summary.total*100) : 0}%`, color:C.teal },
                  { label:'Time',    val:`${((summary.duration||0)/1000).toFixed(1)}s`, color:C.blue },
                ].map(s => (
                  <div key={s.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'10px 18px', textAlign:'center', minWidth:80 }}>
                    <div style={{ color:s.color, fontWeight:800, fontSize:20, fontFamily:'monospace' }}>{s.val}</div>
                    <div style={{ color:C.gray, fontSize:11 }}>{s.label}</div>
                  </div>
                ))}
                {isRunning && currentTest && (
                  <div style={{ flex:1, background:'rgba(59,130,246,0.08)', border:`1px solid rgba(59,130,246,0.3)`, borderRadius:10, padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
                    <Spinner/>
                    <div>
                      <div style={{ color:C.white, fontSize:12, fontWeight:600 }}>{currentTest.name}</div>
                      <div style={{ color:C.gray, fontSize:11 }}>{currentTest.type}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Type cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:8 }}>
              {TYPES.map(type => {
                const count = (tests[type]||[]).length;
                const Icon  = TYPE_ICONS[type];
                return (
                  <button key={type} onClick={() => { setActive('tests'); setActiveTab(type); }} style={{
                    background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px', cursor:'pointer', textAlign:'left', borderTop:`2px solid ${TYPE_COLORS[type]}`, transition:'all 0.15s',
                  }}
                  onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.borderTopColor=TYPE_COLORS[type]; e.currentTarget.style.borderColor=TYPE_COLORS[type]+'44'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.borderColor=C.border; e.currentTarget.style.borderTopColor=TYPE_COLORS[type]; }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <Icon size={14} style={{ color:TYPE_COLORS[type] }}/>
                      <span style={{ color:TYPE_COLORS[type], fontFamily:'monospace', fontWeight:800, fontSize:20 }}>{count}</span>
                    </div>
                    <div style={{ color:C.white, fontSize:11, fontWeight:600, textTransform:'uppercase' }}>{type}</div>
                  </button>
                );
              })}
            </div>

            {/* Run button */}
            <div style={{ marginTop:16, display:'flex', gap:10 }}>
              <button onClick={onRun} disabled={isRunning||!canRun} style={{
                flex:1, padding:'12px', borderRadius:10, border:'none', cursor: isRunning||!canRun ? 'not-allowed' : 'pointer',
                background: isRunning||!canRun ? 'rgba(255,255,255,0.06)' : `linear-gradient(135deg,${C.teal},${C.blue})`,
                color: isRunning||!canRun ? C.gray : C.bg, fontSize:14, fontWeight:700,
                display:'flex', alignItems:'center', justifyContent:'center', gap:8,
              }}>
                <Play size={15}/>{isRunning ? 'Running…' : `Run All ${totalTests} Tests`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TEST PLANS PANEL
// ─────────────────────────────────────────────────────────────
function TestPlansPanel({ tests, results }) {
  const [plans,       setPlans]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('testitnow_plans') || '[]'); } catch { return []; }
  });
  const [planName,   setPlanName]   = useState('');
  const [iteration,  setIteration]  = useState('v1.0');
  const [framework,  setFramework]  = useState('playwright');
  const [activePlan, setActivePlan] = useState(null);

  const savePlan = () => {
    if (!planName.trim()) return;
    const totalTests = Object.values(tests).reduce((s,a)=>s+a.length,0);
    const plan = {
      id: Date.now(),
      name: planName.trim(),
      iteration: iteration.trim() || 'v1.0',
      createdAt: new Date().toISOString(),
      totalTests,
      totalResults: results.length,
      passed: results.filter(r=>r.status==='passed').length,
      failed: results.filter(r=>r.status==='failed').length,
      passRate: results.length ? Math.round(results.filter(r=>r.status==='passed').length/results.length*100) : 0,
      framework,
      // Store snapshot of result IDs and statuses
      resultSnapshot: results.map(r=>({ id:r.id, name:r.name, status:r.status, duration:r.duration })),
    };
    const updated = [plan, ...plans].slice(0, 20);
    setPlans(updated);
    try { localStorage.setItem('testitnow_plans', JSON.stringify(updated)); } catch {}
    setActivePlan(plan);
    setPlanName('');
  };

  const downloadPlanScripts = (plan) => {
    const scripts = getAllTestPlans(tests, results, plan.name, plan.iteration);
    const fw = plan.framework || 'playwright';
    dl(scripts[fw], `${plan.name.replace(/[^a-z0-9]/gi,'_')}-${plan.iteration}-${fw}.spec.js`, 'text/javascript');
    setTimeout(() => dl(scripts.config.packageJson, 'package.json', 'application/json'), 300);
    setTimeout(() => dl(scripts.config.envExample, '.env.example', 'text/plain'), 600);
    setTimeout(() => dl(scripts.config.playwright,  'playwright.config.js', 'text/javascript'), 900);
  };

  const downloadAllFrameworks = (plan) => {
    const scripts = getAllTestPlans(tests, results, plan.name, plan.iteration);
    ['playwright','cypress','jest','k6'].forEach((fw, i) => {
      setTimeout(() => {
        dl(scripts[fw], `${fw}/${plan.name.replace(/[^a-z0-9]/gi,'_')}-${plan.iteration}.${fw==='jest'?'test.js':'spec.js'}`, 'text/javascript');
      }, i * 400);
    });
    setTimeout(() => dl(scripts.config.packageJson, 'package.json', 'application/json'), 1800);
    setTimeout(() => dl(scripts.config.envExample, '.env.example', 'text/plain'), 2200);
    setTimeout(() => dl(scripts.config.playwright, 'playwright.config.js', 'text/javascript'), 2600);
  };

  const fws = [
    { id:'playwright', label:'Playwright', color:'#e5534b', desc:'UI + API + Security' },
    { id:'cypress',    label:'Cypress',    color:'#17a974', desc:'E2E browser tests' },
    { id:'jest',       label:'Jest',       color:'#c21325', desc:'API + Unit tests' },
    { id:'k6',         label:'k6',         color:'#7d64ff', desc:'Performance / load' },
  ];

  return (
    <PanelShell title="Test Plans & Iterations">
      {/* New plan form */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px', marginBottom:14 }}>
        <div style={{ color:C.white, fontSize:13, fontWeight:700, marginBottom:12 }}>Save Current Test Run as Plan</div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          <input value={planName} onChange={e=>setPlanName(e.target.value)} placeholder="Plan name e.g. Sprint 12 Regression"
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, color:C.white, fontSize:13, padding:'8px 12px', outline:'none', fontFamily:'inherit' }}/>
          <div style={{ display:'flex', gap:8 }}>
            <input value={iteration} onChange={e=>setIteration(e.target.value)} placeholder="Iteration e.g. v1.2"
              style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, color:C.white, fontSize:13, padding:'8px 12px', outline:'none', fontFamily:'inherit' }}/>
            <select value={framework} onChange={e=>setFramework(e.target.value)}
              style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, color:C.white, fontSize:13, padding:'8px 10px', outline:'none' }}>
              {fws.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <button onClick={savePlan} disabled={!planName.trim()} style={{
            padding:'9px', borderRadius:8, border:'none', cursor: planName ? 'pointer' : 'not-allowed',
            background: planName ? `linear-gradient(135deg,${C.teal},${C.blue})` : 'rgba(255,255,255,0.06)',
            color: planName ? C.bg : C.gray, fontSize:13, fontWeight:700,
          }}>Save Plan & Generate Scripts</button>
        </div>
      </div>

      {/* Framework selector */}
      <div style={{ marginBottom:12 }}>
        <div style={{ color:C.gray, fontSize:11, fontWeight:600, textTransform:'uppercase', marginBottom:8 }}>Script Frameworks</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
          {fws.map(f => (
            <div key={f.id} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:f.color }}/>
                <span style={{ color:C.white, fontSize:12, fontWeight:700 }}>{f.label}</span>
              </div>
              <div style={{ color:C.gray, fontSize:11 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Saved plans */}
      <div style={{ color:C.gray, fontSize:11, fontWeight:600, textTransform:'uppercase', marginBottom:8 }}>Saved Plans ({plans.length})</div>
      <div style={{ display:'flex', flexDirection:'column', gap:6, overflowY:'auto' }}>
        {plans.length === 0 && (
          <div style={{ textAlign:'center', padding:'24px', color:C.gray }}>
            <Package size={28} style={{ marginBottom:8, opacity:0.4 }}/>
            <p style={{ fontSize:12 }}>No plans saved yet.</p>
          </div>
        )}
        {plans.map(plan => (
          <div key={plan.id} style={{ background:C.card, border:`1px solid ${activePlan?.id===plan.id ? 'rgba(0,212,170,0.4)' : C.border}`, borderRadius:10, padding:'12px', cursor:'pointer', transition:'border-color 0.15s' }}
            onClick={() => setActivePlan(activePlan?.id===plan.id ? null : plan)}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
              <div>
                <div style={{ color:C.white, fontSize:12, fontWeight:700 }}>{plan.name}</div>
                <div style={{ color:C.gray, fontSize:10 }}>{plan.iteration} · {new Date(plan.createdAt).toLocaleDateString()}</div>
              </div>
              <div style={{ display:'flex', gap:4 }}>
                <span style={{ background:`rgba(${plan.passed===plan.totalResults?'0,212,170':'248,113,113'},0.15)`, color: plan.passed===plan.totalResults ? C.teal : '#f87171', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20 }}>
                  {plan.passRate}%
                </span>
              </div>
            </div>
            <div style={{ display:'flex', gap:12, fontSize:11, color:C.gray }}>
              <span>🧪 {plan.totalTests} tests</span>
              <span style={{ color:'#4ade80' }}>✓ {plan.passed}</span>
              <span style={{ color:'#f87171' }}>✗ {plan.failed}</span>
            </div>
            {activePlan?.id === plan.id && (
              <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.border}`, display:'flex', flexDirection:'column', gap:6 }}>
                <button onClick={e=>{ e.stopPropagation(); downloadPlanScripts(plan); }} style={{ ...btnStyle(C.teal), justifyContent:'center' }}>
                  <Terminal size={12}/> Download {plan.framework} Scripts
                </button>
                <button onClick={e=>{ e.stopPropagation(); downloadAllFrameworks(plan); }} style={{ ...btnStyle(C.blue), justifyContent:'center' }}>
                  <Package size={12}/> Download All Frameworks (4 files)
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </PanelShell>
  );
}

// ─────────────────────────────────────────────────────────────
// TEST CASES PANEL
// ─────────────────────────────────────────────────────────────
function TestCasesPanel({ tests, activeTab, setActiveTab, onDelete, onRun, isRunning, canRun, totalTests }) {
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);
  const [scripts,  setScripts]  = useState(null); // { test, fw, code }

  const allTests = TYPES.flatMap(type => (tests[type]||[]).map(t=>({...t,type})));
  const shown    = (activeTab==='all' ? allTests : (tests[activeTab]||[]).map(t=>({...t,type:activeTab})))
    .filter(t => !search || `${t.name||''}${t.id||''}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
    {/* Test case detail modal — outside PanelShell so it isn't clipped */}
    {expanded && (() => {
      const test = shown.find(t => t.id === expanded);
      if (!test) return null;
      return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:24, backdropFilter:'blur(6px)' }}
          onClick={() => setExpanded(null)}>
          <div style={{ background:C.card, borderRadius:16, width:'100%', maxWidth:720, maxHeight:'88vh', display:'flex', flexDirection:'column', overflow:'hidden', border:`1px solid ${C.border}`, boxShadow:'0 32px 80px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:12, flexShrink:0, background:C.sidebar }}>
              <div style={{ width:4, height:32, borderRadius:2, background:TYPE_COLORS[test.type], flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:C.white, fontSize:15, fontWeight:700, lineHeight:1.3 }}>{test.name}</div>
                <div style={{ display:'flex', gap:8, marginTop:4, flexWrap:'wrap' }}>
                  <span style={{ color:C.gray, fontSize:11, fontFamily:'monospace' }}>{test.id}</span>
                  <span style={{ color:TYPE_COLORS[test.type], fontSize:11, textTransform:'uppercase', fontWeight:600 }}>{test.type}</span>
                  <span style={{ fontSize:11, padding:'1px 7px', borderRadius:20, background: test.priority==='Critical'?'rgba(239,68,68,0.15)':'rgba(255,255,255,0.08)', color: test.priority==='Critical'?'#f87171':C.gray }}>{test.priority}</span>
                </div>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={() => { dl(generateScript(test,'playwright'), `${(test.id||'tc').replace(/[^a-z0-9]/gi,'_')}.spec.js`, 'text/javascript'); }}
                  style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:7, border:`1px solid ${C.teal}44`, background:`${C.teal}18`, color:C.teal, cursor:'pointer', fontSize:12 }}>
                  <Terminal size={12}/> Script
                </button>
                <button onClick={() => setExpanded(null)} style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:7, color:C.gray, cursor:'pointer', padding:'6px 10px', fontSize:18, lineHeight:1 }}>✕</button>
              </div>
            </div>
            <div style={{ overflowY:'auto', padding:'20px', display:'flex', flexDirection:'column', gap:14 }}>
              {[['Description',test.description],['Preconditions',test.preconditions],['Test Steps',test.testSteps],['Test Data',test.testData],['Expected Result',test.expectedResult],['URL',test.url]]
                .filter(([,v])=>v).map(([label,value])=>(
                <div key={label}>
                  <div style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>{label}</div>
                  <div style={{ color:C.white, fontSize:13, background:C.bg, borderRadius:8, padding:'12px 14px', lineHeight:1.7, border:`1px solid ${C.border}` }}>
                    {label==='Test Steps'
                      ? String(value).split(' | ').map((s,i)=>(
                          <div key={i} style={{ display:'flex', gap:10, marginBottom:i<String(value).split(' | ').length-1?5:0 }}>
                            <span style={{ color:C.teal, fontWeight:700, minWidth:20, fontFamily:'monospace', fontSize:12 }}>{i+1}.</span>
                            <span>{s.replace(/^\d+\.\s*/,'')}</span>
                          </div>
                        ))
                      : String(value)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    })()}

    <PanelShell title={`Test Cases (${totalTests})`} action={
      <button onClick={onRun} disabled={isRunning||!canRun} style={{ padding:'5px 12px', borderRadius:7, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.teal},${C.blue})`, color:C.bg, fontSize:12, fontWeight:700, display:'flex', alignItems:'center', gap:5 }}>
        <Play size={12}/> Run
      </button>
    }>
      {/* Type filter */}
      <div style={{ display:'flex', gap:3, flexWrap:'wrap', marginBottom:10 }}>
        {['all',...TYPES].map(t => (
          <button key={t} onClick={()=>setActiveTab(t)} style={{ padding:'3px 9px', borderRadius:20, border:'none', cursor:'pointer', fontSize:11, background: activeTab===t ? C.teal : 'rgba(255,255,255,0.06)', color: activeTab===t ? C.bg : C.gray, fontWeight: activeTab===t ? 700 : 400 }}>
            {t==='all'?`All (${totalTests})`:`${t} (${(tests[t]||[]).length})`}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position:'relative', marginBottom:10 }}>
        <Search size={12} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:C.gray }}/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
          style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, color:C.white, fontSize:12, padding:'7px 10px 7px 28px', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}/>
      </div>

      {/* Script viewer modal */}
      {scripts && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div style={{ background:C.sidebar, border:`1px solid ${C.border}`, borderRadius:16, width:'90%', maxWidth:800, maxHeight:'85vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
            <div style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ color:C.white, fontSize:14, fontWeight:700 }}>{scripts.test.id} — {scripts.fw} script</div>
                <div style={{ color:C.gray, fontSize:11 }}>{scripts.test.name}</div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => navigator.clipboard?.writeText(scripts.code)} style={{ ...btnStyle(C.teal), padding:'5px 12px' }}><Copy size={11}/> Copy</button>
                <button onClick={() => dl(scripts.code, `${(scripts.test.id||'tc').replace(/[^a-z0-9]/gi,'_')}.${scripts.fw==='jest'?'test.js':'spec.js'}`, 'text/javascript')} style={{ ...btnStyle(C.blue), padding:'5px 12px' }}><Download size={11}/> Download</button>
                <button onClick={() => setScripts(null)} style={{ background:'none', border:'none', color:C.gray, cursor:'pointer' }}><X size={16}/></button>
              </div>
            </div>
            <div style={{ display:'flex', gap:0, borderBottom:`1px solid ${C.border}` }}>
              {['playwright','cypress','jest','k6'].map(fw => (
                <button key={fw} onClick={() => setScripts({ ...scripts, fw, code: generateScript(scripts.test, fw) })} style={{
                  flex:1, padding:'8px', border:'none', cursor:'pointer', fontSize:11, fontWeight:600,
                  background: scripts.fw===fw ? C.tealDim : 'transparent',
                  color: scripts.fw===fw ? C.teal : C.gray,
                  borderBottom: scripts.fw===fw ? `2px solid ${C.teal}` : '2px solid transparent',
                }}>{fw}</button>
              ))}
            </div>
            <pre style={{ flex:1, overflow:'auto', padding:'16px', fontSize:11, lineHeight:1.7, color:'#a8d8b9', background:C.bg, margin:0, fontFamily:'monospace' }}>
              {scripts.code}
            </pre>
          </div>
        </div>
      )}


      {/* Test list */}
      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
        {shown.length === 0 ? (
          <div style={{ textAlign:'center', padding:'32px', color:C.gray }}>
            <Layers size={28} style={{ marginBottom:8, opacity:0.4 }}/>
            <p style={{ fontSize:12 }}>No tests match.</p>
          </div>
        ) : shown.map(test => (
          <div key={test.id} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, overflow:'hidden', cursor:'pointer', transition:'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = TYPE_COLORS[test.type]+'55'}
            onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
            <div style={{ display:'flex', alignItems:'center', padding:'9px 12px', gap:8 }}
              onClick={() => setExpanded(test.id)}>
              <div style={{ width:3, height:28, borderRadius:2, background:TYPE_COLORS[test.type], flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:C.white, fontSize:12, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{test.name}</div>
                <div style={{ color:C.gray, fontSize:10, marginTop:1, display:'flex', gap:8 }}>
                  <span style={{ fontFamily:'monospace' }}>{test.id}</span>
                  {test.testSteps && <span style={{ color:C.teal, fontSize:10 }}>{test.testSteps.split(' | ').length} steps</span>}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                <span style={{ fontSize:10, padding:'2px 6px', borderRadius:20, background: test.priority==='Critical'?'rgba(239,68,68,0.15)':test.priority==='High'?'rgba(249,115,22,0.12)':'rgba(255,255,255,0.06)', color: test.priority==='Critical'?'#f87171':test.priority==='High'?'#fb923c':C.gray }}>{test.priority}</span>
                <button onClick={e=>{ e.stopPropagation(); dl(generateScript(test,'playwright'), `${(test.id||'tc').replace(/[^a-z0-9]/gi,'_')}.spec.js`, 'text/javascript'); }}
                  style={{ background:'none', border:'none', color:C.blue, cursor:'pointer', padding:3 }} title="Download Playwright script"><Download size={11}/></button>
                <button onClick={e=>{ e.stopPropagation(); onDelete(test.type, test.id); }}
                  style={{ background:'none', border:'none', color:'#ef444466', cursor:'pointer', padding:3 }}
                  onMouseEnter={e=>e.currentTarget.style.color='#f87171'} onMouseLeave={e=>e.currentTarget.style.color='#ef444466'}><Trash2 size={11}/></button>
                <ChevronRight size={12} style={{ color:C.gray }}/>
              </div>
            </div>
          </div>
        ))}
      </div>
    </PanelShell>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// RESULTS PANEL — with screenshots embedded in downloads
// ─────────────────────────────────────────────────────────────
function ResultsPanel({ results, onReportBug, onRetest, tests }) {
  const [expanded, setExpanded] = useState(null);
  const [filter,   setFilter]   = useState('all');

  const shown = filter==='all' ? results : results.filter(r=>r.status===filter);
  const passed = results.filter(r=>r.status==='passed').length;
  const failed = results.filter(r=>r.status==='failed').length;

  // Download HTML report with embedded screenshots and full test details
  const downloadHTMLReport = () => {
    const allTests = Object.entries(tests||{}).flatMap(([type,list])=>list.map(t=>({...t,type})));
    const passed = results.filter(r=>r.status==='passed').length;
    const failed = results.filter(r=>r.status==='failed').length;
    const passRate = results.length ? Math.round(passed/results.length*100) : 0;

    // Build result rows using string concat — no nested backticks
    const resultRows = results.map(r => {
      const tc = allTests.find(t => t.id===r.id||t.name===r.name)||{};
      const steps = (tc.testSteps||r.testSteps||'').split(' | ').filter(Boolean);
      const actual = r.status==='passed'
        ? (tc.expectedResult||'Test passed — actual matches expected')
        : (r.error||'Assertion failed — actual result did not match expected');

      const stepsHtml = steps.length
        ? '<div class="field"><div class="field-label">Test Steps</div><ul class="steps-list">'
          + steps.map((s,i) => {
              const ok = r.status==='passed' || i < steps.length-1;
              return '<li><div class="sc ' + (ok?'ok':'fail') + '">' + (ok?'✓':'✗') + '</div>'
                + '<span>' + s.replace(/^\d+\.\s*/,'') + '</span></li>';
            }).join('')
          + '</ul></div>'
        : '';

      const descHtml     = tc.description ? '<div class="field"><div class="field-label">Description</div><div class="field-value">' + tc.description + '</div></div>' : '';
      const precondsHtml = tc.preconditions ? '<div class="field"><div class="field-label">Preconditions / Assumptions</div><div class="field-value">' + tc.preconditions + '</div></div>' : '';
      const dataHtml     = tc.testData ? '<div class="field"><div class="field-label">Test Data</div><div class="field-value mono">' + tc.testData + '</div></div>' : '';
      const errorHtml    = r.error ? '<div class="field"><div class="field-label" style="color:#f87171">Error</div><div class="field-value" style="color:#fca5a5;border-color:rgba(248,113,113,0.2)">' + r.error + '</div></div>' : '';

      const ssHtml = r.screenshot
        ? '<img src="' + r.screenshot + '" alt="' + (r.name||'') + '" class="screenshot" onclick="(function(src,t){var b=atob(src.split(\',\')[1]),a=new Uint8Array(b.length);for(var i=0;i<b.length;i++)a[i]=b.charCodeAt(i);var u=URL.createObjectURL(new Blob([a],{type:\'image/png\'}));window.open(u,\'_blank\');setTimeout(()=>URL.revokeObjectURL(u),3000)})(this.src,\''+((r.name||'').replace(/'/g,''))+'\')"/><p style="color:#6b7fa3;font-size:10px;text-align:center;margin-top:6px">Click to open full size</p>'
        : '<div class="no-ss"><div style="font-size:24px;margin-bottom:8px">📷</div><div style="font-weight:600;color:#94a3b8;margin-bottom:4px">No screenshot</div><div>Add SCREENSHOTONE_KEY for real screenshots</div></div>';

      return '<div class="result ' + r.status + '">'
        + '<div class="rh"><div class="dot"></div><div class="name">' + (r.name||'') + '</div>'
        + '<span class="badge ' + r.status + '">' + (r.status||'').toUpperCase() + '</span>'
        + '<span style="color:#6b7fa3;font-size:11px;font-family:monospace;margin-left:8px">' + (r.duration||0) + 'ms</span></div>'
        + '<div class="rbody">'
        + '<div class="rleft">'
        + '<div class="meta-grid">'
        + '<div class="meta-box"><div class="mk">Test ID</div><div class="mv">' + (r.id||tc.id||'—') + '</div></div>'
        + '<div class="meta-box"><div class="mk">Type</div><div class="mv">' + (r.type||tc.type||'').toUpperCase() + '</div></div>'
        + '<div class="meta-box"><div class="mk">Priority</div><div class="mv">' + (tc.priority||'—') + '</div></div>'
        + '<div class="meta-box"><div class="mk">Timestamp</div><div class="mv">' + (r.timestamp ? new Date(r.timestamp).toLocaleString() : '—') + '</div></div>'
        + '</div>'
        + descHtml + stepsHtml + precondsHtml + dataHtml
        + '<div class="ev-grid">'
        + '<div><div class="ev-label">Expected</div><div class="ev-box expected">' + (tc.expectedResult||'—') + '</div></div>'
        + '<div><div class="ev-label">Actual</div><div class="ev-box actual ' + r.status + '">' + actual + '</div></div>'
        + '</div>'
        + errorHtml
        + '</div>'
        + '<div class="rright"><div class="field-label" style="margin-bottom:8px">📸 Screenshot</div>' + ssHtml + '</div>'
        + '</div></div>';
    }).join('');

    const statRows = [['Total',results.length,'#f0f4ff'],['Passed',passed,'#4ade80'],['Failed',failed,'#f87171'],['Pass Rate',passRate+'%','#00d4aa'],['Test Cases',allTests.length,'#3b82f6']]
      .map(([l,v,c]) => '<div class="stat"><div class="v" style="color:'+c+'">'+v+'</div><div class="l">'+l+'</div></div>').join('');

    const html = '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">'
      + '<title>TestItNow — Test Results Report</title><style>'
      + '*{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,BlinkMacSystemFont,sans-serif;background:#0a0f1e;color:#f0f4ff;line-height:1.5}'
      + '.header{background:#0d1530;border-bottom:1px solid rgba(0,212,170,0.15);padding:16px 28px;display:flex;justify-content:space-between;align-items:center}'
      + '.logo{font-size:18px;font-weight:900}.logo span{color:#00d4aa}'
      + '.summary{display:flex;gap:12px;padding:16px 28px;flex-wrap:wrap;border-bottom:1px solid rgba(0,212,170,0.1)}'
      + '.stat{background:#111b3a;border:1px solid rgba(0,212,170,0.12);border-radius:10px;padding:10px 18px;text-align:center}'
      + '.stat .v{font-size:20px;font-weight:800;font-family:monospace}.stat .l{font-size:10px;color:#6b7fa3;margin-top:2px}'
      + '.body{padding:20px 28px}.result{border-radius:10px;margin-bottom:16px;overflow:hidden;border:1px solid rgba(255,255,255,0.08)}'
      + '.result.passed{border-color:rgba(74,222,128,0.25);background:rgba(74,222,128,0.03)}'
      + '.result.failed{border-color:rgba(248,113,113,0.25);background:rgba(248,113,113,0.03)}'
      + '.rh{display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid rgba(255,255,255,0.06)}'
      + '.rh .dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}.passed .dot{background:#4ade80}.failed .dot{background:#f87171}'
      + '.rh .name{font-size:13px;font-weight:600;flex:1}'
      + '.badge{display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700}'
      + '.badge.passed{background:rgba(74,222,128,0.15);color:#4ade80}.badge.failed{background:rgba(248,113,113,0.15);color:#f87171}'
      + '.rbody{display:grid;grid-template-columns:1fr 1fr;gap:0}'
      + '.rleft{padding:14px 16px;border-right:1px solid rgba(255,255,255,0.06)}.rright{padding:14px 16px}'
      + '.field{margin-bottom:10px}.field-label{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#6b7fa3;margin-bottom:4px}'
      + '.field-value{font-size:12px;color:#d1d9f0;background:rgba(255,255,255,0.04);border-radius:6px;padding:8px 10px;border:1px solid rgba(255,255,255,0.06);line-height:1.6}'
      + '.field-value.mono{font-family:monospace;font-size:11px}'
      + '.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:10px}'
      + '.meta-box{background:rgba(255,255,255,0.04);border-radius:6px;padding:7px 9px;border:1px solid rgba(255,255,255,0.06)}'
      + '.meta-box .mk{font-size:9px;font-weight:700;text-transform:uppercase;color:#6b7fa3;margin-bottom:2px}'
      + '.meta-box .mv{font-size:11px;font-weight:600;font-family:monospace;color:#f0f4ff}'
      + '.ev-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:10px}'
      + '.ev-box{border-radius:6px;padding:8px 10px;font-size:11px;line-height:1.6}.ev-label{font-size:9px;font-weight:700;text-transform:uppercase;color:#6b7fa3;margin-bottom:4px}'
      + '.expected{background:rgba(74,222,128,0.06);border:1px solid rgba(74,222,128,0.2);color:#d1fae5}'
      + '.actual.passed{background:rgba(74,222,128,0.06);border:1px solid rgba(74,222,128,0.2);color:#d1fae5}'
      + '.actual.failed{background:rgba(248,113,113,0.06);border:1px solid rgba(248,113,113,0.2);color:#fee2e2}'
      + '.steps-list{list-style:none;display:flex;flex-direction:column;gap:5px}'
      + '.steps-list li{display:flex;align-items:flex-start;gap:8px;font-size:11px;color:#d1d9f0}'
      + '.sc{width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:9px;font-weight:bold;margin-top:1px}'
      + '.sc.ok{background:rgba(74,222,128,0.2);color:#4ade80}.sc.fail{background:rgba(248,113,113,0.2);color:#f87171}'
      + '.screenshot{max-width:100%;border-radius:8px;border:1px solid rgba(0,212,170,0.15);display:block;cursor:pointer}'
      + '.no-ss{background:rgba(255,255,255,0.03);border:1px dashed rgba(255,255,255,0.12);border-radius:8px;padding:24px;text-align:center;color:#6b7fa3;font-size:12px}'
      + '.section-title{font-size:16px;font-weight:700;color:#f0f4ff;margin:24px 0 12px;padding-bottom:6px;border-bottom:1px solid rgba(0,212,170,0.12)}'
      + '</style></head><body>'
      + '<div class="header"><div class="logo">TestIt<span>Now</span> — Full Test Report</div>'
      + '<div style="color:#6b7fa3;font-size:12px">Generated: ' + new Date().toLocaleString() + '</div></div>'
      + '<div class="summary">' + statRows + '</div>'
      + '<div class="body"><div class="section-title">Test Results</div>'
      + resultRows
      + '</div></body></html>';

    const blob = new Blob([html], { type:'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href:url, download:'TestItNow_Report_' + new Date().toISOString().split('T')[0] + '.html', style:'display:none' });
    document.body.appendChild(a); a.click();
    setTimeout(()=>{ document.body.removeChild(a); URL.revokeObjectURL(url); }, 400);
  };

  return (
    <>
    {/* Result detail modal */}
    {expanded !== null && results[expanded] && (() => {
      const r  = results[expanded];
      const allTC = Object.values(tests||{}).flat();
      const tc = allTC.find(t => t.id===r.id||t.name===r.name)||{};
      return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20, backdropFilter:'blur(6px)' }}
          onClick={() => setExpanded(null)}>
          <div style={{ background:C.card, borderRadius:16, width:'100%', maxWidth:900, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden', border:`1px solid ${C.border}`, boxShadow:'0 32px 80px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:12, background:C.sidebar, flexShrink:0 }}>
              <div style={{ width:36, height:36, borderRadius:8, background: r.status==='passed'?'rgba(74,222,128,0.15)':'rgba(248,113,113,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                {r.status==='passed' ? <CheckCircle size={18} style={{ color:'#4ade80' }}/> : <XCircle size={18} style={{ color:'#f87171' }}/>}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:C.white, fontSize:14, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name||tc.name}</div>
                <div style={{ display:'flex', gap:10, marginTop:3, flexWrap:'wrap' }}>
                  <span style={{ color:r.status==='passed'?'#4ade80':'#f87171', fontSize:11, fontWeight:700, textTransform:'uppercase' }}>{r.status}</span>
                  <span style={{ color:C.gray, fontSize:11 }}>ID: <span style={{ fontFamily:'monospace', color:C.grayLight }}>{r.id||tc.id||'—'}</span></span>
                  <span style={{ color:C.gray, fontSize:11 }}>🕐 {r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}</span>
                </div>
              </div>
              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                {r.screenshot && <button onClick={() => dlScreenshot(r.screenshot, r.name)} style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 10px', borderRadius:7, border:`1px solid ${C.teal}44`, background:`${C.teal}18`, color:C.teal, cursor:'pointer', fontSize:11 }}><Download size={11}/> PNG</button>}
                {r.status==='failed' && <>
                  <button onClick={() => { onReportBug(r); setExpanded(null); }} style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 10px', borderRadius:7, border:'1px solid rgba(248,113,113,0.3)', background:'rgba(248,113,113,0.1)', color:'#f87171', cursor:'pointer', fontSize:11 }}><Bug size={11}/> Bug</button>
                  <button onClick={() => { onRetest(r); setExpanded(null); }} style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 10px', borderRadius:7, border:`1px solid ${C.blue}44`, background:`${C.blue}18`, color:C.blue, cursor:'pointer', fontSize:11 }}><RotateCcw size={11}/> Retest</button>
                </>}
                <button onClick={() => setExpanded(null)} style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:7, color:C.gray, cursor:'pointer', padding:'6px 10px', fontSize:16 }}>✕</button>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', flex:1, overflow:'hidden' }}>
              <div style={{ overflowY:'auto', padding:'16px', borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ background:r.status==='passed'?'rgba(74,222,128,0.08)':'rgba(248,113,113,0.08)', border:`1px solid ${r.status==='passed'?'rgba(74,222,128,0.25)':'rgba(248,113,113,0.25)'}`, borderRadius:8, padding:'10px 14px' }}>
                  <div style={{ color:r.status==='passed'?'#4ade80':'#f87171', fontSize:12, fontWeight:700 }}>{r.status==='passed'?'✓ Test Passed':'✗ Test Failed'}</div>
                  {r.error && <div style={{ color:'#fca5a5', fontSize:11, marginTop:4 }}>{r.error}</div>}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                  {[['Test ID',r.id||tc.id||'—'],['Type',(r.type||tc.type||'').toUpperCase()],['Priority',tc.priority||'—'],['Duration',`${r.duration||0}ms`],['Timestamp',r.timestamp?new Date(r.timestamp).toLocaleString():'—'],['Retest',r.isRetest?'Yes':'No']].map(([k,v])=>(
                    <div key={k} style={{ background:C.bg, borderRadius:7, padding:'8px 10px', border:`1px solid ${C.border}` }}>
                      <div style={{ color:C.gray, fontSize:9, fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>{k}</div>
                      <div style={{ color:C.white, fontSize:12, fontWeight:600, fontFamily:'monospace' }}>{v}</div>
                    </div>
                  ))}
                </div>
                {tc.description && <div><div style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Description</div><div style={{ color:C.white, fontSize:12, background:C.bg, borderRadius:8, padding:'10px 12px', border:`1px solid ${C.border}`, lineHeight:1.6 }}>{tc.description}</div></div>}
                {(tc.testSteps||r.testSteps) && (
                  <div>
                    <div style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', marginBottom:6 }}>Test Steps</div>
                    <div style={{ background:C.bg, borderRadius:8, padding:'10px 12px', border:`1px solid ${C.border}`, display:'flex', flexDirection:'column', gap:6 }}>
                      {(tc.testSteps||r.testSteps||'').split(' | ').filter(Boolean).map((s,i)=>(
                        <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                          <div style={{ width:20, height:20, borderRadius:'50%', background:r.status==='passed'?'rgba(74,222,128,0.2)':'rgba(248,113,113,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                            <span style={{ color:r.status==='passed'?'#4ade80':'#f87171', fontSize:9, fontWeight:700 }}>{r.status==='passed'?'✓':i<(tc.testSteps||'').split(' | ').length-1?'✓':'✗'}</span>
                          </div>
                          <span style={{ color:C.white, fontSize:12, lineHeight:1.5 }}>{s.replace(/^\d+\.\s*/,'')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {tc.preconditions && <div><div style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Preconditions / Assumptions</div><div style={{ color:C.white, fontSize:12, background:C.bg, borderRadius:8, padding:'10px 12px', border:`1px solid ${C.border}`, lineHeight:1.6 }}>{tc.preconditions}</div></div>}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <div>
                    <div style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Expected Result</div>
                    <div style={{ background:'rgba(74,222,128,0.05)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:8, padding:'10px 12px', color:'#d1fae5', fontSize:12, lineHeight:1.6, minHeight:60 }}>{tc.expectedResult||'—'}</div>
                  </div>
                  <div>
                    <div style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Actual Result</div>
                    <div style={{ background:r.status==='passed'?'rgba(74,222,128,0.05)':'rgba(248,113,113,0.05)', border:`1px solid ${r.status==='passed'?'rgba(74,222,128,0.2)':'rgba(248,113,113,0.2)'}`, borderRadius:8, padding:'10px 12px', color:r.status==='passed'?'#d1fae5':'#fee2e2', fontSize:12, lineHeight:1.6, minHeight:60 }}>
                      {r.status==='passed'?(tc.expectedResult||'Test passed'):(r.error||'Assertion failed')}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ overflowY:'auto', padding:'16px', display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase' }}>📸 Screenshot</div>
                {r.screenshot
                  ? <><img src={r.screenshot} alt={r.name} style={{ width:'100%', borderRadius:10, border:`1px solid ${C.border}`, cursor:'pointer', display:'block' }} onClick={()=>openScreenshot(r.screenshot, r.name)}/>
                    <button onClick={()=>dlScreenshot(r.screenshot,r.name)} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'8px', borderRadius:8, border:`1px solid ${C.teal}44`, background:`${C.teal}18`, color:C.teal, cursor:'pointer', fontSize:12 }}><Download size={13}/> Download PNG</button></>
                  : <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:'32px 20px', textAlign:'center' }}>
                      <div style={{ fontSize:32, marginBottom:12 }}>📷</div>
                      <div style={{ color:C.white, fontSize:13, fontWeight:600, marginBottom:8 }}>No screenshot</div>
                      <div style={{ color:C.gray, fontSize:11, lineHeight:1.7 }}>Add <span style={{ color:C.teal, fontFamily:'monospace' }}>SCREENSHOTONE_KEY</span> to Vercel backend env vars.</div>
                    </div>
                }
              </div>
            </div>
          </div>
        </div>
      );
    })()}

    <PanelShell title={'Results (' + results.length + ')'} action={
      <button onClick={downloadHTMLReport} disabled={!results.length} style={{ padding:'5px 10px', borderRadius:7, border:'none', cursor:'pointer', background:'rgba(0,212,170,0.1)', color:C.teal, fontSize:11, fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
        <Download size={11}/> HTML
      </button>
    }>
      {results.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom:10 }}>
          {[{ label:'Passed', val:passed, color:'#4ade80' }, { label:'Failed', val:failed, color:'#f87171' }, { label:'Rate', val:`${results.length?Math.round(passed/results.length*100):0}%`, color:C.teal }].map(s=>(
            <div key={s.label} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'8px', textAlign:'center' }}>
              <div style={{ color:s.color, fontWeight:800, fontSize:16, fontFamily:'monospace' }}>{s.val}</div>
              <div style={{ color:C.gray, fontSize:10 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display:'flex', gap:4, marginBottom:10 }}>
        {['all','passed','failed'].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{ flex:1, padding:'5px', borderRadius:7, border:'none', cursor:'pointer', fontSize:11, background:filter===f?C.teal:'rgba(255,255,255,0.06)', color:filter===f?C.bg:C.gray, fontWeight:filter===f?700:400 }}>
            {f==='all'?`All (${results.length})`:f==='passed'?`✓ ${passed}`:`✗ ${failed}`}
          </button>
        ))}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {shown.length===0
          ? <div style={{ textAlign:'center', padding:'32px', color:C.gray }}><p style={{ fontSize:12 }}>No results yet.</p></div>
          : shown.map((r,i)=>(
            <div key={i} onClick={()=>setExpanded(results.indexOf(r))}
              style={{ background:r.status==='passed'?'rgba(74,222,128,0.05)':'rgba(248,113,113,0.05)', border:`1px solid ${r.status==='passed'?'rgba(74,222,128,0.2)':'rgba(248,113,113,0.2)'}`, borderRadius:8, padding:'9px 12px', cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
              {r.status==='passed'?<CheckCircle size={14} style={{ color:'#4ade80', flexShrink:0 }}/>:<XCircle size={14} style={{ color:'#f87171', flexShrink:0 }}/>}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:C.white, fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</div>
                <div style={{ color:C.gray, fontSize:10 }}>{r.duration}ms {r.screenshot&&'· 📸'}</div>
              </div>
              <div style={{ display:'flex', gap:4 }}>
                {r.status==='failed'&&<><button onClick={e=>{e.stopPropagation();onReportBug(r);}} style={{ background:'none', border:'none', color:'#f87171', cursor:'pointer', padding:3 }}><Bug size={11}/></button><button onClick={e=>{e.stopPropagation();onRetest(r);}} style={{ background:'none', border:'none', color:C.blue, cursor:'pointer', padding:3 }}><RotateCcw size={11}/></button></>}
                <ChevronRight size={12} style={{ color:C.gray }}/>
              </div>
            </div>
          ))
        }
      </div>
    </PanelShell>
    </>
  );
}
// ─────────────────────────────────────────────────────────────
function CreateTestPanel({ onAdd }) {
  const [type, setType]   = useState('ui');
  const [fw,   setFw]     = useState('playwright');
  const [data, setData]   = useState({ id:'', name:'', description:'', priority:'High', preconditions:'', testSteps:'', testData:'', expectedResult:'' });
  const set = (k,v) => setData(p=>({...p,[k]:v}));

  const handle = () => {
    if (!data.name.trim()) return alert('Title is required');
    const id = data.id || `TC-${type.toUpperCase()}-${Date.now()}`;
    onAdd(type, { id, name:data.name, description:data.description, priority:data.priority, preconditions:data.preconditions, testSteps:data.testSteps, testData:data.testData, expectedResult:data.expectedResult });
    setData({ id:'', name:'', description:'', priority:'High', preconditions:'', testSteps:'', testData:'', expectedResult:'' });
  };

  const preview = () => {
    const script = generateScript({ ...data, type }, fw);
    dl(script, `${(data.id||'new_test').replace(/[^a-z0-9]/gi,'_')}.${fw==='jest'?'test.js':'spec.js'}`, 'text/javascript');
  };

  return (
    <PanelShell title="Create Test Case" action={
      <button onClick={handle} style={{ padding:'5px 12px', borderRadius:7, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.teal},${C.blue})`, color:C.bg, fontSize:12, fontWeight:700 }}>+ Add</button>
    }>
      <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
        {/* Type */}
        <div>
          <label style={lbl}>Test Type</label>
          <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
            {TYPES.map(t => (
              <button key={t} onClick={()=>setType(t)} style={{ padding:'4px 10px', borderRadius:20, border:'none', cursor:'pointer', fontSize:11, background: type===t?C.teal:'rgba(255,255,255,0.06)', color: type===t?C.bg:C.gray, fontWeight: type===t?700:400 }}>{t}</button>
            ))}
          </div>
        </div>

        {[
          { k:'id',             l:'Test ID (optional)',   ph:'TC-UI-001',                           ta:false },
          { k:'name',           l:'Title *',              ph:'Login with valid credentials',          ta:false },
          { k:'description',    l:'Description',          ph:'Verify the login form works',           ta:true  },
          { k:'preconditions',  l:'Preconditions',        ph:'User on login page | Form visible',     ta:true  },
          { k:'testSteps',      l:'Test Steps',           ph:'1. Enter email | 2. Enter password | 3. Click Login', ta:true },
          { k:'testData',       l:'Test Data',            ph:'Email: test@test.com | Pass: Pass123!', ta:false },
          { k:'expectedResult', l:'Expected Result',      ph:'Login succeeds | Redirect to dashboard', ta:true },
        ].map(f => (
          <div key={f.k}>
            <label style={lbl}>{f.l}</label>
            {f.ta
              ? <textarea value={data[f.k]} onChange={e=>set(f.k,e.target.value)} placeholder={f.ph} rows={2} style={inp}/>
              : <input    value={data[f.k]} onChange={e=>set(f.k,e.target.value)} placeholder={f.ph}         style={inp}/>
            }
          </div>
        ))}

        <div>
          <label style={lbl}>Priority</label>
          <select value={data.priority} onChange={e=>set('priority',e.target.value)} style={inp}>
            {['Critical','High','Medium','Low'].map(p=><option key={p}>{p}</option>)}
          </select>
        </div>

        {/* Script framework + preview */}
        <div>
          <label style={lbl}>Script Framework</label>
          <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:8 }}>
            {['playwright','cypress','jest','k6'].map(f => (
              <button key={f} onClick={()=>setFw(f)} style={{ padding:'3px 9px', borderRadius:20, border:'none', cursor:'pointer', fontSize:11, background: fw===f?C.blue:'rgba(255,255,255,0.06)', color: fw===f?C.white:C.gray }}>{f}</button>
            ))}
          </div>
          <button onClick={preview} disabled={!data.name} style={{ ...btnStyle(C.blue), width:'100%', justifyContent:'center', opacity: data.name?1:0.5 }}>
            <Terminal size={12}/> Download {fw} script
          </button>
        </div>

        <button onClick={handle} style={{ padding:'11px', borderRadius:9, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.teal},${C.blue})`, color:C.bg, fontSize:14, fontWeight:700 }}>
          + Add Test Case
        </button>
      </div>
    </PanelShell>
  );
}

// ─────────────────────────────────────────────────────────────
// TEMPLATE PANEL
// ─────────────────────────────────────────────────────────────
function TemplatePanel({ onAdd }) {
  const [imported, setImported] = useState(0);
  const [over,     setOver]     = useState(false);

  const parse = text => {
    try {
      const lines = text.split('\n');
      const heads = lines[0].split(',').map(h=>h.trim().replace(/^"|"$/g,'').toLowerCase());
      const nameI = heads.findIndex(h=>h.includes('title')||h.includes('name'));
      const typeI = heads.findIndex(h=>h==='type');
      const priI  = heads.findIndex(h=>h.includes('priority'));
      const idI   = heads.findIndex(h=>h==='id'||h.includes('test case id')||h.includes('test id'));
      const descI = heads.findIndex(h=>h.includes('desc'));
      const stpsI = heads.findIndex(h=>h.includes('step'));
      const expI  = heads.findIndex(h=>h.includes('expect'));
      let n = 0;
      lines.slice(1).filter(l=>l.trim()).forEach((line,i)=>{
        const v = line.split(',').map(x=>x.trim().replace(/^"|"$/g,''));
        const rawType = (v[typeI]||'').toLowerCase();
        const type = ['ui','api','security','performance','database','unit'].includes(rawType) ? rawType : 'unit';
        onAdd(type, { id:v[idI]||`TC-IMP-${Date.now()}-${i}`, name:v[nameI]||`Imported ${i+1}`, description:v[descI]||'', priority:v[priI]||'Medium', testSteps:v[stpsI]||'', expectedResult:v[expI]||'' });
        n++;
      });
      setImported(p=>p+n);
    } catch(e) { alert('CSV parse error: ' + e.message); }
  };

  const handleFile = f => { const r=new FileReader(); r.onload=e=>parse(e.target.result); r.readAsText(f); };

  const dlTemplate = () => {
    const rows = [
      ['Test Case ID','Title','Description','Type','Priority','Preconditions','Test Steps','Expected Result'],
      ['TC-001','Login valid credentials','Test login form','ui','Critical','Login page open','1. Enter email | 2. Enter password | 3. Click Login','Login succeeds | Redirect to dashboard'],
      ['TC-002','GET /api/health','API health check','api','High','Server running','GET /api/health | Verify 200','Status 200 | {"status":"ok"}'],
      ['TC-003','SQL injection login','Security test','security','Critical','Login form visible',"Enter ' OR '1'='1 | Submit",'Input rejected | No DB error'],
    ];
    dl('\uFEFF'+rows.map(r=>r.map(c=>`"${c}"`).join(',')).join('\r\n'), 'TestItNow_Template.csv', 'text/csv;charset=utf-8');
  };

  const presets = [
    { label:'OWASP Top 10',      count:10, type:'security',    desc:'SQL injection, XSS, CSRF, Auth bypass…' },
    { label:'REST API Baseline', count:12, type:'api',         desc:'CRUD, auth, pagination, validation…' },
    { label:'UI Smoke Tests',    count:10, type:'ui',          desc:'Page load, responsive, forms, nav…' },
    { label:'Core Web Vitals',   count:8,  type:'performance', desc:'LCP, FID, CLS, TTFB benchmarks…' },
  ];

  return (
    <PanelShell title="Templates & Import">
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        <button onClick={dlTemplate} style={{ ...btnStyle(C.teal), justifyContent:'center', padding:'9px' }}>
          <Download size={13}/> Download CSV Template
        </button>

        {/* Drop zone */}
        <div onDragOver={e=>{e.preventDefault();setOver(true);}} onDragLeave={()=>setOver(false)} onDrop={e=>{e.preventDefault();setOver(false);const f=e.dataTransfer.files[0];if(f)handleFile(f);}}
          style={{ border:`2px dashed ${over?C.teal:C.border}`, borderRadius:12, padding:'28px', textAlign:'center', background:over?'rgba(0,212,170,0.05)':'transparent', transition:'all 0.2s' }}>
          <FileUp size={24} style={{ color:over?C.teal:C.gray, marginBottom:8 }}/>
          <p style={{ color:C.grayLight, fontSize:13, marginBottom:6 }}>Drag & drop CSV</p>
          <label style={{ cursor:'pointer' }}>
            <span style={{ color:C.teal, fontSize:12, fontWeight:600, border:`1px solid ${C.border}`, padding:'5px 14px', borderRadius:7, background:'rgba(0,212,170,0.08)' }}>Browse</span>
            <input type="file" accept=".csv,.txt" style={{ display:'none' }} onChange={e=>{if(e.target.files[0])handleFile(e.target.files[0]);e.target.value='';}}/>
          </label>
        </div>

        {imported > 0 && (
          <div style={{ background:'rgba(0,212,170,0.08)', border:`1px solid rgba(0,212,170,0.25)`, borderRadius:8, padding:'9px 12px', display:'flex', alignItems:'center', gap:8 }}>
            <CheckCircle size={14} style={{ color:C.teal }}/>
            <span style={{ color:C.teal, fontSize:12, fontWeight:600 }}>{imported} test cases imported!</span>
          </div>
        )}

        <div style={{ color:C.gray, fontSize:11, fontWeight:600, textTransform:'uppercase' }}>Quick Presets</div>
        {presets.map(p => (
          <button key={p.label} onClick={()=>{
            Array.from({length:p.count}).forEach((_,i)=>{
              onAdd(p.type,{ id:`PRESET-${p.type.toUpperCase()}-${Date.now()}-${i}`, name:`${p.label} — Test ${i+1}`, description:p.desc, priority:'High', testSteps:'Follow test procedure', expectedResult:'Test passes' });
            });
            setImported(x=>x+p.count);
          }} style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', transition:'border-color 0.15s' }}
          onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(0,212,170,0.35)'}
          onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
            <div>
              <div style={{ color:C.white, fontSize:12, fontWeight:600 }}>{p.label}</div>
              <div style={{ color:C.gray, fontSize:11 }}>{p.desc}</div>
            </div>
            <span style={{ color:C.teal, fontSize:11, fontWeight:700, background:'rgba(0,212,170,0.1)', padding:'2px 8px', borderRadius:20 }}>+{p.count}</span>
          </button>
        ))}
      </div>
    </PanelShell>
  );
}

// ─────────────────────────────────────────────────────────────
// INTEGRATIONS PANEL
// ─────────────────────────────────────────────────────────────
function IntegrationsPanel({ onJiraImport, onJiraExport, onImport }) {
  return (
    <PanelShell title="Integrations">
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {[
          { icon:'🔵', name:'Jira', desc:'Import/export test cases & bugs',
            actions:[{ label:'Import from Jira', fn:onJiraImport }, { label:'Export to Jira', fn:onJiraExport }] },
          { icon:'☁️', name:'Azure DevOps', desc:'Sync with Azure Test Plans',
            actions:[{ label:'Import from Azure', fn:onImport }] },
          { icon:'📊', name:'CSV / Excel', desc:'Bulk import from spreadsheet',
            actions:[{ label:'Import CSV', fn:onImport }] },
        ].map(int => (
          <div key={int.name} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <span style={{ fontSize:18 }}>{int.icon}</span>
              <div>
                <div style={{ color:C.white, fontSize:13, fontWeight:600 }}>{int.name}</div>
                <div style={{ color:C.gray, fontSize:11 }}>{int.desc}</div>
              </div>
            </div>
            <div style={{ display:'flex', gap:6 }}>
              {int.actions.map(a => (
                <button key={a.label} onClick={a.fn} style={{ ...btnStyle(C.teal), flex:1, justifyContent:'center' }}>{a.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PanelShell>
  );
}

// ─────────────────────────────────────────────────────────────
// SHARED PANEL SHELL
// ─────────────────────────────────────────────────────────────
function PanelShell({ title, children, action }) {
  return (
    <div style={{ width:400, flexShrink:0, background:C.panel, borderLeft:`1px solid ${C.border}`, height:'100vh', display:'flex', flexDirection:'column', position:'sticky', top:0, boxShadow:'-4px 0 20px rgba(0,0,0,0.06)' }}>
      <div style={{ padding:'14px 16px 12px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, background:C.sidebar }}>
        <h2 style={{ color:C.white, fontSize:14, fontWeight:700 }}>{title}</h2>
        {action}
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'12px 14px' }}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STYLE CONSTANTS
// ─────────────────────────────────────────────────────────────
const btnStyle = (color) => ({
  display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:7,
  border:`1px solid ${color}22`, background:`${color}18`,
  color, fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s',
});
const lbl = { display:'block', color:C.gray, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 };
const inp = { width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, color:C.white, fontSize:12, padding:'8px 10px', fontFamily:'inherit', outline:'none', resize:'vertical', boxSizing:'border-box' };

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────
export default function AppShell({
  theme = 'light', setTheme,
  userProfile, userPlan, freeRunsLeft, isAuthenticated,
  onLogout, onUpgrade, onReports, onApiKeys,
  tests, results, bugs, activeTab, setActiveTab,
  onAdd, onDelete, onRun, onReportBug, onRetest,
  isRunning, currentTest, totalTests, summary, canRun,
  onAnalyze, isGenerating, progress, progressMsg,
  onExport, onJiraImport, onJiraExport, onImport,
  showAds,
}) {
  const [activePanel, setActivePanel] = useState('analyze');
  const T = getTheme(theme);
  updateTheme(T); // sync module-level C with current theme

  const TC = {
    bg:       C.bg,
    sidebar:  C.sidebar,
    panel:    C.panel,
    card:     C.card,
    border:   C.border,
    teal:     C.teal,
    tealDim:  C.tealDim,
    blue:     C.blue,
    white:    C.white,
    gray:     C.grayLight,
    grayLight:C.grayLight,
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:C.bg, fontFamily:"-apple-system, BlinkMacSystemFont, 'Inter', sans-serif", color:C.white }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;} input::placeholder,textarea::placeholder{color:${C.gray};} ::-webkit-scrollbar{width:4px;height:4px;} ::-webkit-scrollbar-thumb{background:${C.border};border-radius:3px;} select option{background:${C.card};color:${C.white};}`}</style>

      <Sidebar
        active={activePanel} setActive={setActivePanel}
        userProfile={userProfile} userPlan={userPlan}
        onLogout={onLogout} onUpgrade={onUpgrade} onReports={onReports} onApiKeys={onApiKeys}
        totalTests={totalTests} results={results}
        T={T} theme={theme} setTheme={setTheme}
      />

      <AnalyzeHero
        T={T}
        onAnalyze={onAnalyze} isGenerating={isGenerating} progress={progress} progressMsg={progressMsg}
        totalTests={totalTests} setActive={setActivePanel}
        activeTab={activeTab} setActiveTab={setActiveTab}
        tests={tests} results={results} summary={summary}
        isRunning={isRunning} currentTest={currentTest} onRun={onRun} canRun={canRun}
      />

      {activePanel === 'analyze'      && totalTests > 0 && <ResultsPanel results={results} onReportBug={onReportBug} onRetest={onRetest} tests={tests}/>}
      {activePanel === 'tests'        && <TestCasesPanel tests={tests} activeTab={activeTab} setActiveTab={setActiveTab} onDelete={onDelete} onRun={onRun} isRunning={isRunning} canRun={canRun} totalTests={totalTests}/>}
      {activePanel === 'results'      && <ResultsPanel   results={results} onReportBug={onReportBug} onRetest={onRetest} tests={tests}/>}
      {activePanel === 'plans'        && <TestPlansPanel tests={tests} results={results}/>}
      {activePanel === 'create'       && <CreateTestPanel onAdd={onAdd}/>}
      {activePanel === 'template'     && <TemplatePanel   onAdd={onAdd}/>}
      {activePanel === 'integrations' && <IntegrationsPanel onJiraImport={onJiraImport} onJiraExport={onJiraExport} onImport={onImport}/>}
    </div>
  );
}
